import { useState, useEffect, useRef, useCallback } from 'preact/hooks'
import { Monitor, Tablet, Smartphone, FileText, Heart, Loader2, AlertCircle, RefreshCw } from 'lucide-preact'
import { RocketLaunch } from './RocketLaunch'
import type { ComponentChildren } from 'preact'
import { describeGenerationFailure } from '@/utils/generationFailure'
import type { GenerationFailureDto } from '@/types/api-generated'

import { GenerationFailurePanel, FAILURE_PRIMARY_BUTTON } from './GenerationFailurePanel'
import { injectForcedEventState, injectSandboxStubs, replaceYouTubeEmbeds, replaceDownloadLinks, replaceCalendlyEmbeds, type EventState } from '@/utils/htmlPreviewStubs'
import type { QuizDto } from '@/api/webs'

/** The thank-you page keeps its own sentence for a failure nobody classified. */
function thankYouFailureText(failure: GenerationFailureDto | string | null | undefined): string {
    const view = describeGenerationFailure(failure)
    if (view.kind === 'unknown') return 'Something went wrong generating the thank you page. Please try again.'
    return view.font ? `${view.title}. ${view.message}` : view.message
}

type Viewport = 'desktop' | 'tablet' | 'mobile'
type Phase = 'loading' | 'blasting-off' | 'ready'

const activeButtonClass = 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
const inactiveButtonClass = 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'

const viewportWidths: Record<Viewport, number> = {
    desktop: 1280,
    tablet: 768,
    mobile: 375,
}

interface HtmlPreviewProps {
    html: string | null
    thankYouHtml?: string | null
    loading?: boolean
    thankYouGenerating?: boolean
    generationError?: string | null
    /**
     * What to show when the first build failed, chosen by the builder because it knows the
     * person's role and owns the fixes. Without it the failure shows with Retry only.
     */
    failureContent?: ComponentChildren
    /** The same failure as a strip above a page that already exists, when a rebuild of it failed. */
    failureBanner?: ComponentChildren
    thankYouGenerationError?: string | null
    thankYouGenerationFailure?: GenerationFailureDto | null
    onRetry?: () => void
    canGenerateThankYou?: boolean
    onGenerateThankYou?: () => void
    /**
     * Whether the failed thank-you page may be generated again from its error view. The builder decides: Generate starts
     * a template's thank-you page again from the template, and designs any other page's from nothing, which is only a
     * retry when there was no thank-you page to lose.
     */
    canRetryThankYou?: boolean
    /**
     * Webster paused to ask design questions before building. Holds the launch sequence
     * rather than dropping to the empty/error state — the generation has not failed and
     * has not finished, it is waiting on the user, and tearing the sequence down would
     * read as failure.
     */
    awaitingDesignInput?: boolean
    /**
     * Whether the web's pages have a before and an after: a scheduled Online Event. Only those are worth
     * previewing in two states — a Meeting, Demo or Download page has exactly one, and the toggle offered a
     * second that never existed (ORB-826). An On-demand recording never airs, so it has one too, and a
     * live-then-on-demand landing page changes after the event, so the toggle covers both pages (ORB-1076).
     */
    hasEventStates?: boolean
    /**
     * The web's working quiz (ORB-916), which the API's preview writes into the page. When set, the preview gets a
     * pin for each quiz step on the Landing Page tab and for each band on the Thank You tab, so a reviewer sees every
     * screen a visitor can reach. Null for a web without a quiz.
     */
    quiz?: QuizDto | null
}

/**
 * Pins the quiz runtime (QuizRuntime.js on the API side) to one step or one band, like injectForcedEventState. The
 * names are the runtime's contract: `__OX_QUIZ_STEP` is 'intro', 'details' or 'question:<id>', and `__OX_QUIZ_BAND`
 * is a band id. The value is JSON-encoded because ids come from the author.
 */
const injectQuizPin = (rawHtml: string, name: '__OX_QUIZ_STEP' | '__OX_QUIZ_BAND', value: string): string => {
    const script = `<script>window.${name}=${JSON.stringify(value).replace(/</g, '\\u003c')};</script>`
    return rawHtml.includes('</head>')
        ? rawHtml.replace('</head>', `${script}</head>`)
        : `${script}${rawHtml}`
}

export function HtmlPreview({ html, thankYouHtml, loading, thankYouGenerating, generationError, failureContent, failureBanner, thankYouGenerationError, thankYouGenerationFailure, onRetry, canGenerateThankYou, onGenerateThankYou, canRetryThankYou, awaitingDesignInput, hasEventStates, quiz }: HtmlPreviewProps) {
    const [viewport, setViewport] = useState<Viewport>('desktop')
    const [phase, setPhase] = useState<Phase>(loading ? 'loading' : 'ready')
    const [activePage, setActivePage] = useState<'landing' | 'thankYou'>('landing')
    const [forceState, setForceState] = useState<EventState>('pre')
    const [quizStep, setQuizStep] = useState('intro')
    const [quizBand, setQuizBand] = useState<string | null>(null)
    // Buffer HTML so we don't reveal it until blast-off completes
    const [displayHtml, setDisplayHtml] = useState<string | null>(loading ? null : html)
    const phaseRef = useRef<Phase>(loading ? 'loading' : 'ready')
    const pendingHtmlRef = useRef<string | null>(null)
    const blastOffCompleteRef = useRef(false)

    useEffect(() => {
        // Generation failed at any point in the lifecycle — jump straight to the
        // terminal 'ready' phase so the error UI below renders instead of the
        // rocket animation looping indefinitely. Without this, a first-gen
        // failure leaves the blast-off animation playing with no html to reveal.
        if (generationError) {
            if (phaseRef.current !== 'ready') {
                phaseRef.current = 'ready'
                setPhase('ready')
            }
            // A failed rebuild of an existing page keeps showing that page, including a font re-baked into it.
            if (html) setDisplayHtml(html)
            return
        }

        if (loading) {
            phaseRef.current = 'loading'
            blastOffCompleteRef.current = false
            setPhase('loading')
            setDisplayHtml(null)
            setActivePage('landing')
        } else if (phaseRef.current === 'loading') {
            // Loading finished — start blast-off. HTML may arrive during animation.
            if (html) pendingHtmlRef.current = html
            phaseRef.current = 'blasting-off'
            setPhase('blasting-off')
        } else if (phaseRef.current === 'blasting-off') {
            // Mid blast-off — buffer html as it arrives
            if (html) pendingHtmlRef.current = html
            // If animation already finished and html just arrived, transition now
            if (html && blastOffCompleteRef.current) {
                phaseRef.current = 'ready'
                setDisplayHtml(html)
                pendingHtmlRef.current = null
                setPhase('ready')
                blastOffCompleteRef.current = false
            }
        } else {
            // Ready state — show html directly
            phaseRef.current = 'ready'
            setPhase('ready')
            setDisplayHtml(html)
        }
    }, [loading, html, generationError])

    const handleBlastOffComplete = useCallback(() => {
        blastOffCompleteRef.current = true
        if (pendingHtmlRef.current) {
            phaseRef.current = 'ready'
            setDisplayHtml(pendingHtmlRef.current)
            pendingHtmlRef.current = null
            setPhase('ready')
        }
        // No html yet — stay on blast-off screen. Effect will catch it when html arrives.
    }, [])

    // A page without states has nothing to pin — it renders as published.
    // The preview only fills the quiz in when it has questions and bands, so pins are offered on the same terms.
    const quizQuestions = quiz?.Questions ?? []
    const quizBands = quiz?.Bands ?? []
    const showStepPins = quizQuestions.length > 0 && quizBands.length > 0
    const activeBand = quizBands.find(band => band.Id === quizBand)?.Id ?? quizBands[0]?.Id ?? null
    const pinnedThankYouHtml = thankYouHtml && showStepPins && activeBand
        ? injectQuizPin(thankYouHtml, '__OX_QUIZ_BAND', activeBand)
        : thankYouHtml
    const thankYouDisplayHtml = pinnedThankYouHtml
        ? (hasEventStates ? injectForcedEventState(pinnedThankYouHtml, forceState) : pinnedThankYouHtml)
        : null
    // Applied at render time rather than into the buffered displayHtml, so a pin change never replays the launch.
    const quizPinnedLandingHtml = displayHtml && showStepPins ? injectQuizPin(displayHtml, '__OX_QUIZ_STEP', quizStep) : displayHtml
    const landingDisplayHtml = quizPinnedLandingHtml && hasEventStates
        ? injectForcedEventState(quizPinnedLandingHtml, forceState)
        : quizPinnedLandingHtml
    const rawActiveHtml = activePage === 'landing' ? landingDisplayHtml : thankYouDisplayHtml
    const activeHtml = rawActiveHtml ? injectSandboxStubs(replaceDownloadLinks(replaceCalendlyEmbeds(replaceYouTubeEmbeds(rawActiveHtml)))) : null

    const viewportButtons: Array<{ key: Viewport; icon: typeof Monitor; label: string }> = [
        { key: 'desktop', icon: Monitor, label: 'Desktop' },
        { key: 'tablet', icon: Tablet, label: 'Tablet' },
        { key: 'mobile', icon: Smartphone, label: 'Mobile' },
    ]

    // Checked before the phase states: parking happens with no page yet and no generation
    // in flight, which every other branch would read as "nothing is happening".
    if (awaitingDesignInput && !displayHtml) {
        return <RocketLaunch phase="holding" />
    }

    if (phase === 'loading') {
        return <RocketLaunch phase="idle" />
    }

    if (phase === 'blasting-off') {
        return <RocketLaunch phase="blasting-off" onComplete={handleBlastOffComplete} />
    }

    if (!displayHtml && generationError) {
        const view = describeGenerationFailure(generationError)
        return (
            <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900 px-6">
                {failureContent ?? (
                    <GenerationFailurePanel
                        view={view}
                        actions={onRetry && view.offerRetry ? (
                            <button
                                type="button"
                                onClick={onRetry}
                                disabled={loading}
                                aria-busy={loading}
                                className={FAILURE_PRIMARY_BUTTON}
                            >
                                <RefreshCw className="w-4 h-4" />
                                {loading ? 'Retrying…' : 'Retry'}
                            </button>
                        ) : undefined}
                    />
                )}
            </div>
        )
    }

    if (!displayHtml) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900 text-sm text-slate-500 dark:text-slate-400">
                No preview available
            </div>
        )
    }

    const showThankYouSpinner = activePage === 'thankYou' && thankYouGenerating && !thankYouHtml
    const showThankYouError = activePage === 'thankYou' && thankYouGenerationError && !thankYouHtml
    const showThankYouCta = activePage === 'thankYou' && !thankYouHtml && !thankYouGenerating && !thankYouGenerationError && canGenerateThankYou

    return (
        <div className="h-full flex flex-col">
            {/* Viewport toolbar */}
            <div className="flex items-center gap-1 px-4 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 shrink-0">
                {viewportButtons.map(({ key, icon: Icon, label }) => (
                    <button
                        key={key}
                        type="button"
                        title={label}
                        onClick={() => setViewport(key)}
                        className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                            viewport === key ? activeButtonClass : inactiveButtonClass
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        <span>{label}</span>
                    </button>
                ))}

                {/* Page switcher — pushed to right */}
                <div className="ml-auto border-l border-slate-200 dark:border-slate-700 pl-2 flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => setActivePage('landing')}
                        className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                            activePage === 'landing' ? activeButtonClass : inactiveButtonClass
                        }`}
                    >
                        <FileText className="w-3.5 h-3.5" />
                        <span>Landing Page</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActivePage('thankYou')}
                        className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                            activePage === 'thankYou' ? activeButtonClass : inactiveButtonClass
                        }`}
                    >
                        <Heart className="w-3.5 h-3.5" />
                        <span>Thank You</span>
                    </button>
                </div>

                {/* State toggle — both pages of a scheduled Online Event, the only web whose pages have states */}
                {hasEventStates && (
                    <div className="border-l border-slate-200 dark:border-slate-700 pl-2 ml-2 flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setForceState('pre')}
                            className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                                forceState === 'pre' ? activeButtonClass : inactiveButtonClass
                            }`}
                        >
                            <span>Pre-event</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setForceState('post')}
                            className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                                forceState === 'post' ? activeButtonClass : inactiveButtonClass
                            }`}
                        >
                            <span>Post-event</span>
                        </button>
                    </div>
                )}
            </div>

            {showStepPins && activePage === 'landing' && (
                <div
                    role="group"
                    aria-label="Preview a quiz step"
                    className="flex items-center gap-1 px-4 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 shrink-0 overflow-x-auto"
                >
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-2">Step</span>
                    <QuizPinButton label="Intro" active={quizStep === 'intro'} onClick={() => setQuizStep('intro')} />
                    {quiz?.DetailsPlacement === 'Before' && (
                        <>
                            <QuizPinDivider />
                            <QuizPinButton label="Details" active={quizStep === 'details'} onClick={() => setQuizStep('details')} />
                        </>
                    )}
                    <QuizPinDivider />
                    {quizQuestions.map((question, index) => {
                        const step = `question:${question.Id}`
                        return (
                            <QuizPinButton
                                key={question.Id}
                                label={String(index + 1)}
                                ariaLabel={`Question ${index + 1}`}
                                numeric
                                active={quizStep === step}
                                onClick={() => setQuizStep(step)}
                            />
                        )
                    })}
                    {quiz?.DetailsPlacement !== 'Before' && (
                        <>
                            <QuizPinDivider />
                            <QuizPinButton label="Details" active={quizStep === 'details'} onClick={() => setQuizStep('details')} />
                        </>
                    )}
                    <span className="ml-auto pl-4 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">Preview only. Answers are not sent.</span>
                </div>
            )}

            {showStepPins && activePage === 'thankYou' && thankYouHtml && (
                <div
                    role="group"
                    aria-label="Preview a quiz band"
                    className="flex items-center gap-1 px-4 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 shrink-0 overflow-x-auto"
                >
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-2">Band</span>
                    {quizBands.map(band => (
                        <QuizPinButton
                            key={band.Id}
                            label={band.Name || band.Id}
                            active={activeBand === band.Id}
                            onClick={() => setQuizBand(band.Id)}
                        />
                    ))}
                    <span className="ml-auto pl-4 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">Sample score for each band. Answers are not sent.</span>
                </div>
            )}

            {activePage === 'landing' && displayHtml && generationError && !loading && failureBanner}

            {/* Iframe container — fills remaining space */}
            <div className="flex-1 flex justify-center bg-slate-100 dark:bg-slate-900 overflow-auto">
                {showThankYouCta ? (
                    <div className="flex flex-col items-center justify-center gap-4 max-w-md text-center px-6">
                        <Heart className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                        <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">No thank you page yet</p>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Generate a thank you page to show after form submission.</p>
                        </div>
                        {onGenerateThankYou && (
                            <button
                                type="button"
                                onClick={onGenerateThankYou}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                            >
                                <Heart className="w-4 h-4" />
                                Generate Thank You Page
                            </button>
                        )}
                    </div>
                ) : showThankYouError ? (
                    <div className="flex flex-col items-center justify-center gap-4 max-w-md text-center px-6">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                        <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Thank you page generation failed</p>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{thankYouFailureText(thankYouGenerationFailure ?? thankYouGenerationError)}</p>
                        </div>
                        {canRetryThankYou && onGenerateThankYou && describeGenerationFailure(thankYouGenerationFailure ?? thankYouGenerationError).offerRetry && (
                            <button type="button" onClick={onGenerateThankYou} className={FAILURE_PRIMARY_BUTTON}>
                                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                                Retry
                            </button>
                        )}
                    </div>
                ) : showThankYouSpinner ? (
                    <div className="flex flex-col items-center justify-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                        <span>Generating thank you page...</span>
                    </div>
                ) : (
                    <iframe
                        srcdoc={activeHtml ?? undefined}
                        sandbox="allow-scripts"
                        title="Landing page preview"
                        style={{ width: `${viewportWidths[viewport]}px`, maxWidth: '100%', height: '100%' }}
                        className="bg-white shadow-sm"
                    />
                )}
            </div>
        </div>
    )
}

function QuizPinButton({ label, ariaLabel, active, numeric, onClick }: {
    label: string
    ariaLabel?: string
    active: boolean
    numeric?: boolean
    onClick: () => void
}) {
    return (
        <button
            type="button"
            aria-pressed={active}
            aria-label={ariaLabel}
            onClick={onClick}
            className={`p-1.5 rounded-md transition-colors text-xs whitespace-nowrap ${numeric ? 'min-w-7 tabular-nums' : ''} ${active ? activeButtonClass : inactiveButtonClass}`}
        >
            {label}
        </button>
    )
}

function QuizPinDivider() {
    return <span aria-hidden="true" className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1 shrink-0" />
}
